import create from 'zustand/vanilla';
import { persist, createJSONStorage } from 'zustand/middleware';

interface SpaceUserV2State {
    user_id: string;
    path: string;
    nickname: string;
    email: string;
    spacehome_create_step: number;
    design_type: 'mall' | 'gonggu' | 'banner' | 'custom' | 'text';
    bg_filter_type: 'none' | 'dark' | 'blur' | 'bright';
    bg_type: 'color' | 'image';
    description: string | null;
    bg_color: string;
    text_color: string;
    button_color: string;
    profile_img_url: string | null;
    banner_img_url: string | null;
    bg_img_url: string | null;
    is_button_animated: boolean;
    block_list: any[];
    active_block: string;
    grade: number;
    grade_name: '일반셀러' | '인플루언서' | '소도몰';
    is_path_editable: boolean;
    path_editable_date: string;
    recommended_mall_banner_image_url: string;
    recommended_gonggu_banner_image_url: string;
    recommended_custom_banner_image_url: string;
    recommended_banner_image_url: string;
    is_using_recommended_mall_banner_image: boolean;
    is_using_recommended_gonggu_banner_image: boolean;
    is_using_recommended_custom_banner_image: boolean;
    is_using_recommended_banner_image: boolean;
}
export interface SpaceUserV2StatePatch {
    user_id?: string;
    path?: string;
    nickname?: string;
    email?: string;
    spacehome_create_step?: number;
    design_type?: 'mall' | 'gonggu' | 'banner' | 'custom' | 'text';
    bg_filter_type?: 'none' | 'dark' | 'blur' | 'bright';
    bg_type?: 'color' | 'image';
    description?: string | null;
    bg_color?: string;
    text_color?: string;
    button_color?: string;
    profile_img_url?: string | null;
    banner_img_url?: string | null;
    bg_img_url?: string | null;
    is_button_animated?: boolean;
    block_list?: any[];
    active_block?: string;
    grade?: number;
    grade_name?: '일반셀러' | '인플루언서' | '소도몰';
    is_path_editable?: boolean;
    path_editable_date?: string;
    recommended_mall_banner_image_url?: string;
    recommended_gonggu_banner_image_url?: string;
    recommended_custom_banner_image_url?: string;
    recommended_banner_image_url?: string;
    is_using_recommended_mall_banner_image?: boolean;
    is_using_recommended_gonggu_banner_image?: boolean;
    is_using_recommended_custom_banner_image?: boolean;
    is_using_recommended_banner_image?: boolean;
}
export type BlockListType =
    | TextBlockType
    | LinkBlockType
    | ScheduleBlockType
    | SocialBlockType
    | GalleryBlockType
    | LineBlockType
    | VideoBlockType
    | PartnersBlockType;
export type BlockFormType =
    | 'text'
    | 'link'
    | 'schedule'
    | 'line'
    | 'video'
    | 'social'
    | 'gallery'
    | 'partners'
    | 'collection';

interface EventBlockDefaultType {
    block_id: string;
}
export interface PartnersItemType {
    partners_id: string;
    block_order_id: number;
    title: string;
    link_url: string;
    img_url: string;
    start_date_time: string;
    end_date_time: string;
    is_valid: boolean;
    on_off: boolean;
    is_activated: boolean;
    created_at: string;
    categories: string[];
    discount_value: number;
    discount_method: 'won' | 'percent';
    product_price: number;
    product_charge: number;
}
export interface PartnersBlockType extends EventBlockDefaultType {
    block_type: 'partners';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        is_activated: boolean;
        title: string;
        sub_title: string;
        partners_content: PartnersItemType[];
        block_style: 'ONE' | 'TWO' | 'THREE';
        show_images_only: boolean;
        categories: string[];
    };
}
export interface TextBlockType extends EventBlockDefaultType {
    block_type: 'text';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        title: string;
        sub_title: string;
        text_align_type: 'left' | 'center' | 'right';
        text_color: string;
    };
}
export interface BadgeType {
    badge_id: string;
    type: 'solid' | 'outline';
    text: string;
    text_color: string;
    shape: 'oval' | 'rectangle';
    bg_color: string;
    border_color: string;
    is_auto_created: boolean;
}
export interface LinkBlockType extends EventBlockDefaultType {
    block_type: 'link' | 'shopping' | 'image';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        title: string;
        link_url: string;
        img_url: string;
        is_sns_link: boolean;
        img_shape: 'circle' | 'square';
        img_size: 'small' | 'big';
        link_label: string;
        link_value: string;
        is_date: boolean;
        start_date_time: string;
        end_date_time: string;
        badge_content: BadgeType[];
        is_matching: boolean;
        matching_start_date_time: string;
        matching_end_date_time: string;
    };
}
export interface ScheduleBlockType extends EventBlockDefaultType {
    block_type: 'schedule';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        schedule_content: {
            schedule_id: string;
            title: string;
            link_url: string;
            start_date_time: string;
            end_date_time: string;
            on_off: boolean;
        }[];
    };
}
export interface SocialBlockType extends EventBlockDefaultType {
    block_type: 'social';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        social_content: {
            social_id: string;
            link_value: string;
            link_label: string;
            link_url: string;
            is_activated: boolean;
        }[];
    };
}
interface GalleryBlockListType {
    gallery_id?: string;
    img_url: string;
    img_ratio: number;
    is_activated?: boolean;
}
export interface GalleryBlockType extends EventBlockDefaultType {
    block_type: 'gallery';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        gallery_content: GalleryBlockListType[];
        aspect_type: 'intrinsic' | 'square';
    };
}

export interface LineBlockType extends EventBlockDefaultType {
    block_type: 'line';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        line_type: 'space' | 'line' | 'dash' | 'zigzag' | 'dot';
        line_color: string;
    };
}

export interface VideoBlockType extends EventBlockDefaultType {
    block_type: 'video';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        title: string;
        video_url: string;
        embed_url: string;
        channel_name: string;
        publish_date: string;
    };
}

export interface CollectionBlockType extends EventBlockDefaultType {
    block_type: 'collection';
    block_content: {
        on_off: boolean;
        is_valid: boolean;
        title: string;
        block_style: 'ONE' | 'TWO' | 'THREE';
        show_images_only: boolean;
        link_blocks: {
            id: string;
            link_url: string;
            title: string;
            img_url: string;
            description?: string;
        }[];
    };
}

export interface SpaceUserV2Store extends SpaceUserV2State {
    setSpaceUserV2Info: (user: SpaceUserV2State) => void;
    patchSpaceUserV2Info: (user: SpaceUserV2StatePatch) => void;
    clearUser: () => void;
}
const initializeState: SpaceUserV2State = {
    user_id: '',
    nickname: '',
    email: '',
    path: '',
    spacehome_create_step: 1,
    banner_img_url: null,
    bg_color: '',
    bg_filter_type: 'none',
    bg_img_url: null,
    bg_type: 'color',
    block_list: [],
    button_color: '',
    description: '',
    design_type: 'gonggu',
    is_button_animated: false,
    profile_img_url: null,
    text_color: '',
    active_block: 'null',
    grade: 0,
    grade_name: '일반셀러',
    is_path_editable: false,
    path_editable_date: '',
    recommended_mall_banner_image_url: '',
    recommended_gonggu_banner_image_url: '',
    recommended_custom_banner_image_url: '',
    recommended_banner_image_url: '',
    is_using_recommended_mall_banner_image: false,
    is_using_recommended_gonggu_banner_image: false,
    is_using_recommended_custom_banner_image: false,
    is_using_recommended_banner_image: false,
};
export const spaceUserV2Store = create<SpaceUserV2Store>()(
    persist(
        (set) => ({
            ...initializeState,
            setSpaceUserV2Info: (user: SpaceUserV2State) => {
                set((state) => user);
            },
            patchSpaceUserV2Info: (user: SpaceUserV2StatePatch) => {
                set((state) => {
                    return {
                        user_id: user?.user_id || state.user_id,
                        nickname: user?.nickname || state.nickname,
                        email: user?.email || state.email,
                        path: user?.path || state.path,
                        banner_img_url:
                            user?.banner_img_url === 'no data' ? null : user?.banner_img_url || state.banner_img_url,
                        bg_color: user?.bg_color === 'no data' ? undefined : user?.bg_color || state.bg_color,
                        bg_filter_type: user?.bg_filter_type || state.bg_filter_type,
                        bg_img_url: user?.bg_img_url === 'no data' ? null : user?.bg_img_url || state.bg_img_url,
                        bg_type: user?.bg_type || state.bg_type,
                        block_list: user?.block_list || state.block_list || [],
                        button_color:
                            user?.button_color === 'no data' ? undefined : user?.button_color || state.button_color,
                        design_type: user?.design_type || state.design_type,
                        description: user?.description === 'no data' ? null : user?.description || state.description,
                        is_button_animated:
                            user?.is_button_animated !== undefined ? user.is_button_animated : state.is_button_animated,
                        profile_img_url:
                            user?.profile_img_url === 'no data' ? null : user?.profile_img_url || state.profile_img_url,
                        spacehome_create_step: user?.spacehome_create_step || state.spacehome_create_step,
                        text_color: user?.text_color || state.text_color,
                        active_block: user?.active_block || state.active_block,
                        grade: user?.grade || state?.grade,
                        grade_name: user?.grade_name || state?.grade_name,
                        is_path_editable:
                            user?.is_path_editable !== undefined ? user.is_path_editable : state.is_path_editable,
                        path_editable_date:
                            user?.path_editable_date !== undefined ? user.path_editable_date : state.path_editable_date,
                        recommended_mall_banner_image_url:
                            user?.recommended_mall_banner_image_url || state?.recommended_mall_banner_image_url,
                        recommended_gonggu_banner_image_url:
                            user?.recommended_gonggu_banner_image_url || state?.recommended_gonggu_banner_image_url,
                        recommended_custom_banner_image_url:
                            user?.recommended_custom_banner_image_url || state?.recommended_custom_banner_image_url,
                        recommended_banner_image_url:
                            user?.recommended_banner_image_url || state?.recommended_banner_image_url,
                        is_using_recommended_mall_banner_image:
                            user?.is_using_recommended_mall_banner_image ??
                            state?.is_using_recommended_mall_banner_image,
                        is_using_recommended_gonggu_banner_image:
                            user?.is_using_recommended_gonggu_banner_image ??
                            state?.is_using_recommended_gonggu_banner_image,
                        is_using_recommended_custom_banner_image:
                            user?.is_using_recommended_custom_banner_image ??
                            state?.is_using_recommended_custom_banner_image,
                        is_using_recommended_banner_image:
                            user?.is_using_recommended_banner_image ?? state?.is_using_recommended_banner_image,
                    };
                });
            },
            clearUser: () => {
                set(initializeState);
            },
        }),
        {
            name: 'space-user-v2-storage',
            storage: createJSONStorage(() => sessionStorage),
        },
    ),
);

export default spaceUserV2Store;
